/* import __COLOCATED_TEMPLATE__ from './sq-image-uploader.hbs.js'; */
import { computed } from '@ember/object';
/* eslint square/no-restricted-files:"off" -- unscoped components in this folder are discouraged */
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { resolve } from 'rsvp';
import ValidatedViewSupport from '@dashboard/shared-ui/mixins/validated-view-support'; // eslint-disable-line ember/no-mixins

/**
`SqImageUploaderComponent` allows you to make image uploaders. It works by
allowing the merchant to click and select an image from a file selector.
It shows an icon for adding an image when the `value` is blank and a preview
when it is not.

The `value` attribute will be set to a File object corresponding to the
file the merchant selects using the file picker. If this value is bound to
an attribute on a model, you can then use the `FormDataSupport` mixin in your
adapter to to automatically submit the file as a multipart request on save.

Within the `SqImageUploaderComponent` is an `SqImageComponent`. Each of the
attributes prefixed with `preview` in the `SqImageUploaderComponent` are
bound to the corresponding attributes of the `SqImageComponent` within.

When the `canSelectImages` attribute is set to false,
`SqImageUploaderComponent` is no longer able to upload images. In this case,
 when SqImageUploader is clicked, the `cantSelectImagesCallback` is called.

## Simple Example

You can create a simple uploader by binding the `value` and `previewUrl`.

```handlebars
{{sq-image-uploader value=controller.profileImage
                    previewUrl=controller.profileImage.square200_url
                    class="l-image-uploader-profile-image"
                    onFileUploaded=(action "onFileUploaded")}}
```
## Advanced Example

You can create a pretty advanced uploader by binding the `canSelectImages`
attribute. In this case, you can optionally pass a `cantSelectImagesCallback`
which will be called when `canSelectImages` evaluates to `false`.

```handlebars
{{sq-image-uploader value=newImage.file
                    canSelectImages=canAddImages
                    cantSelectImagesCallback=(action "showMaxImagesWarning")
                    previewUrl=newImage.url
                    previewWidth=thumbnailWidth
                    previewHeight=thumbnailHeight
                    previewClass="image-gallery-thumbnail"}}
```
*/

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(ValidatedViewSupport, {
  i18n: service(),
  classNames: ['image-uploader'],
  classNameBindings: ['value:image-uploader-is-previewing'],
  value: null,
  reader: null,
  showPreview: true,
  previewUrl: null,
  previewWidth: null,
  previewHeight: null,
  previewClass: null,
  thumbnailAddClass: null,
  canSelectImages: true,
  cantSelectImagesCallback: null,
  showDeleteButton: true,
  showUploadButton: false,
  showUploadButtonOnHover: false,
  showSpinner: false,
  buttonWindowClass: null,
  buttonClass: 'button--blue-link',
  init(...args) {
    this._super(...args);
    if (!this.button) {
      this.set('button', this.i18n.t('button.upload'));
    }
  },
  confirmDelete: () => resolve(true),
  _buttonClass: computed('buttonClass', 'showUploadButtonOnHover', 'showUploadButton', function () {
    let classNameString = this.buttonClass;
    // Force showing upload button if `showUploadButtonOnHover` is true
    if (!this.showUploadButtonOnHover && !this.showUploadButton) {
      classNameString += ' l-hide';
    }
    return classNameString;
  }),
  _thumbnailAddClass: computed('previewUrl', 'thumbnailAddClass', function () {
    if (this.previewUrl) {
      return '';
    }
    return this.thumbnailAddClass || 'image-uploader__thumbnail-add';
  }),
  _logoIsSelectedClass: computed('isVisibleClass', function () {
    // Helper class to determine whether an upload prompt should be rendered
    // Only render a prompt if there is not already an image being previewed
    return this.isVisibleClass ? `image-uploader-preview-container ${this.isVisibleClass}` : 'image-uploader-preview-container';
  }),
  _previewTitle: computed('previewUrl', function () {
    if (this.previewUrl) {
      return this.i18n.t('imageUploader.filledState.title');
    }
    return this.i18n.t('imageUploader.emptyState.title');
  }),
  didInsertElement(...args) {
    this._super(...args);
    this.reader = new FileReader();
    this._bindFileInputListeners();
    this._bindFileReaderLoadListener();
  },
  willDestroyElement(...args) {
    this._super(...args);
    const imageUploader = this.element.querySelector('input[type="file"]');
    imageUploader.removeEventListener('click', this._boundFileInputClickListener);
    imageUploader.removeEventListener('change', this._boundFileInputChangeListener);
    this._boundFileInputClickListener = null;
    this._boundFileInputChangeListener = null;
    if (this.reader) {
      this.reader.onload = null;
    }
  },
  _bindFileInputListeners() {
    const imageUploader = this.element.querySelector('input[type="file"]');
    this._boundFileInputClickListener = e => {
      if (!this.canSelectImages) {
        this.cantSelectImagesCallback?.();
        e.preventDefault();
      }
    };
    imageUploader.addEventListener('click', this._boundFileInputClickListener);
    this._boundFileInputChangeListener = e => {
      this._fileInputChangeListener(e);
    };
    imageUploader.addEventListener('change', this._boundFileInputChangeListener);
  },
  _bindFileReaderLoadListener() {
    this.reader.onload = e => {
      this._fileReaderLoadListener(e);
    };
  },
  _fileInputChangeListener(e) {
    // The `e.files` fallback is only here for testing, as `target` is readonly
    // checking event.originalEvent for the file is necessary for events triggered by jQuery
    let files;
    if (e.target && e.target.files && e.target.files.length > 0) {
      files = e.target.files;
    } else if (e.originalEvent && e.originalEvent.files && e.originalEvent.files.length > 0) {
      files = e.originalEvent.files;
    } else {
      files = e.files;
    }
    const file = files && files[0];
    if (!file) {
      return;
    }
    this.reader.readAsDataURL(file);
    this.set('value', file);
    if (this.onFileUploaded) {
      this.onFileUploaded(file);
    }
  },
  _boundFileInputChangeListener: null,
  _boundFileInputClickListener: null,
  _fileReaderLoadListener(e) {
    this.set('previewUrl', e.target.result);
  },
  actions: {
    openUploadDialog() {
      this.element.querySelector('input[type="file"]').click();
    },
    delete() {
      this.confirmDelete().then(shouldDeleteImage => {
        if (shouldDeleteImage) {
          this.set('value', null);
          this.set('previewUrl', null);
          this.element.querySelector('input[type="file"]').input = null;
          if (this.delete) {
            this.delete();
          }
        }
      });
    }
  }
});